/*
|--------------------------------------------------------------------------
| Table Selection Mixin
|--------------------------------------------------------------------------
|
| Generic multiple row selection behaviour for the listing tables.
| Any table component that holds its rows inside a `list` data property can
| use this mixin to get checkbox selection out of the box.
|
| The mixin exposes the props below, so the host component does not have to
| declare them itself :
|
| selectable         : enables the checkbox column (pass true)
| selectionKey       : the row property used as identifier, dot notation is
|                      supported (ex : "uuid", "id", "user_account.id").
|                      Defaults to "id"
| selectionCondition : optional function (row, index) => boolean, used to
|                      disable the checkbox for the rows that can not be
|                      part of the bulk action
|
| The selection is kept while the user browses through the pages and is
| cleared whenever the page size or the applied filters change.
|
*/
export default {
    props: {
        selectable: {
            default: undefined
        },
        selectionKey: {
            type: String,
            default: 'id'
        },
        selectionCondition: {
            type: Function,
            default: undefined
        }
    },
    data() {
        return {
            /* identifier => row, keeps the rows selected on every visited page */
            selectedRowsMap: {}
        };
    },
    computed: {
        isSelectionEnabled() {
            return typeof this.selectable != 'undefined' && this.selectable !== false;
        },
        /* rows of the page currently displayed, the host component owns `list` */
        selectionSourceList() {
            return Array.isArray(this.list) ? this.list : [];
        },
        selectedKeys() {
            return Object.keys(this.selectedRowsMap);
        },
        selectedRows() {
            return this.selectedKeys.map(key => this.selectedRowsMap[key]);
        },
        selectedCount() {
            return this.selectedKeys.length;
        },
        selectableCurrentPageRows() {
            return this.selectionSourceList.filter(row => this.isRowSelectable(row));
        },
        isCurrentPageFullySelected() {
            let rows = this.selectableCurrentPageRows;
            if (!rows.length) {
                return false;
            }
            return rows.every(row => this.isRowSelected(row));
        }
    },
    methods: {
        /* reads the identifier of a row, dot notation supported */
        resolveRowIdentifier(row) {
            if (!row) {
                return null;
            }
            let key = this.selectionKey ? this.selectionKey : 'id';
            let value = key.split('.').reduce(function (prev, curr) {
                return prev ? prev[curr] : null;
            }, row);

            if (typeof value == 'undefined' || value === null || value === '') {
                return null;
            }
            return String(value);
        },
        isRowSelectable(row) {
            if (typeof this.selectionCondition == 'undefined' || this.selectionCondition === null) {
                return this.resolveRowIdentifier(row) !== null;
            }
            return this.resolveRowIdentifier(row) !== null && this.selectionCondition(row);
        },
        isRowSelected(row) {
            let identifier = this.resolveRowIdentifier(row);
            return identifier !== null && typeof this.selectedRowsMap[identifier] != 'undefined';
        },
        toggleRowSelection(row) {
            let identifier = this.resolveRowIdentifier(row);
            if (identifier === null || !this.isRowSelectable(row)) {
                return;
            }
            if (typeof this.selectedRowsMap[identifier] != 'undefined') {
                this.$delete(this.selectedRowsMap, identifier);
            } else {
                this.$set(this.selectedRowsMap, identifier, row);
            }
            this.emitSelectionChange();
        },
        selectAllFromCurrentPage() {
            this.selectableCurrentPageRows.forEach(row => {
                let identifier = this.resolveRowIdentifier(row);
                if (identifier !== null) {
                    this.$set(this.selectedRowsMap, identifier, row);
                }
            });
            this.emitSelectionChange();
        },
        unselectAllFromCurrentPage() {
            this.selectableCurrentPageRows.forEach(row => {
                let identifier = this.resolveRowIdentifier(row);
                if (identifier !== null) {
                    this.$delete(this.selectedRowsMap, identifier);
                }
            });
            this.emitSelectionChange();
        },
        /* clears every selected row, on every page */
        clearSelection() {
            this.selectedRowsMap = {};
            this.emitSelectionChange();
        },
        emitSelectionChange() {
            this.$emit('selectionChanged', {
                keys: this.selectedKeys,
                rows: this.selectedRows,
                count: this.selectedCount
            });
        }
    }
};
